import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import useActiveDocument from "../../document/hooks/useActiveDocument";
import { useMultipleGroupBlocks } from "../../configMap";
import { EMPTY_ARRAY } from "../../annotation/selectors/annotationsForEntitySelector";
import useDocumentTypes from "./useDocumentTypes";
import {
  configMapIsLoading,
  configMapLoadFailed,
  setConfigMap,
} from "../../configMap/store/configMapSlice";
import { useUpdateDocumentTypeRequestMutation } from "../store/documentTypeApi";
import {
  setCategorizationAnnotationsBatch,
  setIncompleteCategorizations,
  setIncompleteEntities,
} from "../../metadata/store/metadataSlice";
import { mapAnnotationsToAnnotatorFormat } from "../../annotation";
import {
  getSideAndTableControlAnnotationItems,
  getSideAndTableControlAnnotations,
} from "../../annotation/utils/utils";
import { AnnotationItem, DocumentTypeWithAnnotations } from "../interfaces";
import { cleanUpTableAnnotationIndexGaps } from "../../annotation/utils/annotationMappers";
import { setAnnotations } from "../../annotation/store/annotationSlice";
import {
  filterNewAnnotations,
  filterNewCategoryAnnotations,
  getCategorizationIds,
  getEntityIds,
  mapBackToCategorizationAnnotations,
  mapBackToEntityAnnotations,
  mergeNewAnnotations,
  updateAnnotationsWithNewGroupBlockId,
  updateCategorizationAnnotationsWithNewGroupBlockId,
} from "../utils/synchronizeAnnotationHelper";
import { CategorizationAnnotation } from "../../configMap/interfaces/category";
import { Annotation } from "../../annotator/interfaces/annotation";
import { convertToEntityAnnotationDtos } from "../../configMap/utils/entityAnnotationUtils";
import useAnnotationNormalization from "../../configMap/hooks/useAnnotationNormalization";

const useSelectDocumentType = () => {
  const dispatch = useAppDispatch();

  const { activeDocument, activeDocumentMinimal, setActiveDocument } =
    useActiveDocument();
  const { setInitialMultipleGroupBlocksCount } = useMultipleGroupBlocks();
  const { normalizeEntityAnnotationsInBulk } = useAnnotationNormalization();

  const categorizationAnnotations = useAppSelector(
    (state) =>
      state.metadataReducer.categorizationState.categorizationAnnotations,
  );

  const currentConfig = useAppSelector(
    (state) => state.configMapReducer.configMap,
  );

  const annotations = useAppSelector((state) => {
    if (!activeDocument) {
      return EMPTY_ARRAY;
    }

    return state.annotationReducer.tableControlAnnotations.concat(
      state.annotationReducer.sideControlAnnotations,
    );
  });

  const activeDocumentTypeId = useMemo(() => {
    return activeDocumentMinimal?.documentType?.id;
  }, [activeDocumentMinimal]);

  const { documentTypes } = useDocumentTypes();

  const [updateDocumentTypeRequest] = useUpdateDocumentTypeRequestMutation();

  const synchronizeAnnotations = useCallback(
    (documentType: DocumentTypeWithAnnotations) => {
      const configMap = documentType?.configMap;
      const newGroupBlocks = configMap?.groupBlocks;
      const oldGroupBlocks = currentConfig?.groupBlocks;

      const flatCategoryAnnotations:
        Array<CategorizationAnnotation> | undefined =
        documentType.categorizationAnnotations?.map((ca) => ({
          ...ca,
          groupBlockId: ca.groupBlockId ?? undefined,
          index: ca.index,
          originalIndex: ca.index,
        }));

      const mappedAnnotations = mapAnnotationsToAnnotatorFormat(
        documentType.entityAnnotations,
        configMap,
      );

      const newEntityIds = getEntityIds(newGroupBlocks);
      const oldEntityIds = getEntityIds(oldGroupBlocks);
      const newCategorizationIds = getCategorizationIds(
        newGroupBlocks,
        configMap,
      );
      const oldCategorizationIds = getCategorizationIds(
        oldGroupBlocks,
        currentConfig,
      );

      const onlyNewAnnotations = filterNewAnnotations(
        mappedAnnotations,
        newEntityIds,
        oldEntityIds,
      );
      const onlyNewCategoryAnnotations = filterNewCategoryAnnotations(
        flatCategoryAnnotations,
        newCategorizationIds,
        oldCategorizationIds,
        newGroupBlocks,
      );

      const updatedAnnotations = updateAnnotationsWithNewGroupBlockId(
        [...annotations],
        newGroupBlocks,
      );
      const updatedCategorizations =
        updateCategorizationAnnotationsWithNewGroupBlockId(
          [...(categorizationAnnotations || [])],
          newGroupBlocks,
        );

      let annotationItems: Array<AnnotationItem> = [
        ...(updatedAnnotations.map((a) => ({
          ...a,
          isEntity: true,
        })) as Array<AnnotationItem>),
        ...((updatedCategorizations?.map((a) => ({ ...a, isEntity: false })) ??
          []) as Array<AnnotationItem>),
      ];
      const onlyNewAnnotationItems: Array<AnnotationItem> = [
        ...(onlyNewAnnotations.map((a) => ({
          ...a,
          isEntity: true,
        })) as Array<AnnotationItem>),
        ...((onlyNewCategoryAnnotations?.map((a) => ({
          ...a,
          isEntity: false,
        })) ?? []) as Array<AnnotationItem>),
      ];

      annotationItems = mergeNewAnnotations(
        annotationItems,
        onlyNewAnnotationItems,
      );

      // Filter annotations to retain only relevant ones from the new config map
      annotationItems = annotationItems.filter((annotation) => {
        if (annotation.isEntity) {
          return newEntityIds.includes(annotation.entity!.id);
        }
        return newCategorizationIds.includes(annotation.categorizationId!);
      });

      const { sideControlAnnotations, tableControlAnnotations } =
        getSideAndTableControlAnnotationItems(annotationItems);

      const annotationsResult = cleanUpTableAnnotationIndexGaps(
        tableControlAnnotations,
      );

      return sideControlAnnotations.concat(annotationsResult);
    },
    [currentConfig, annotations, categorizationAnnotations],
  );

  const changeDocumentType = useCallback(
    async (documentTypeId: string) => {
      if (!activeDocumentMinimal?.id) {
        return;
      }

      dispatch(configMapIsLoading());

      try {
        const data = await updateDocumentTypeRequest({
          documentId: activeDocumentMinimal.id,
          documentTypeId,
        }).unwrap();

        const annotationsResult = synchronizeAnnotations(data);
        const entityAnnotations: Array<Annotation> =
          mapBackToEntityAnnotations(annotationsResult);
        const categoryAnnotations: Array<CategorizationAnnotation> =
          mapBackToCategorizationAnnotations(annotationsResult);

        const { sideControlAnnotations, tableControlAnnotations } =
          getSideAndTableControlAnnotations(entityAnnotations);

        setActiveDocument({ ...activeDocumentMinimal, documentType: data });

        dispatch(setConfigMap(data.configMap!));
        dispatch(setIncompleteCategorizations([]));
        dispatch(setIncompleteEntities([]));
        dispatch(
          setAnnotations({ tableControlAnnotations, sideControlAnnotations }),
        );
        dispatch(setCategorizationAnnotationsBatch(categoryAnnotations));

        setInitialMultipleGroupBlocksCount(
          data.configMap!,
          sideControlAnnotations.concat(tableControlAnnotations),
          categoryAnnotations,
        );

        const annotationsToNormalize =
          convertToEntityAnnotationDtos(entityAnnotations);
        normalizeEntityAnnotationsInBulk(
          annotationsToNormalize,
          true,
          categoryAnnotations,
          data.configMap,
        );
      } catch (e) {
        dispatch(configMapLoadFailed());
        console.error(e);
      }
    },
    [
      activeDocumentMinimal,
      setActiveDocument,
      setInitialMultipleGroupBlocksCount,
      dispatch,
      updateDocumentTypeRequest,
      synchronizeAnnotations,
      normalizeEntityAnnotationsInBulk,
    ],
  );

  return { activeDocumentTypeId, changeDocumentType, documentTypes };
};

export default useSelectDocumentType;
